<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';

	import jasonElton from './images/Jason-Elton-DSC0060-1.jpg';
	import homeEnergyScore from './images/Resized-Home-Energy-Score-image.png';
</script>

<Section class="max-md:pt-0">
	<Image src={jasonElton} alt="Jason Elton" loading="eager" />
	<p class="italic">
		Indow interviewed Jason Elton from Enhabit, a non-profit offering homeowner
		advising and home upgrade services. Jason leads quality management system
		development for Enhabit. He has played a critical role in evolving and
		implementing building science in Oregon, to the benefit of both contractors
		and homeowners. His interests in energy efficiency developed while working
		with a local construction company building and remodeling homes, ultimately
		leading to his career promoting residential energy efficiency.
	</p>
	<hr />
	<p>
		A Home Energy Score is a measure of home efficiency that reveals
		opportunities for energy-saving improvements and gives homeowners, buyers
		and sellers an objective way of comparing a home to others in the community.
		It’s a quick way to get a professional, certified rating that does not
		involve extensive diagnostic equipment.
	</p>

	<p>
		The Home Energy Score developed by <a
			href="https://betterbuildingssolutioncenter.energy.gov/home-energy-score/value-home-energy-score"
			rel="external">the U.S. Department of Energy</a
		>, uses a 1 through 10 scale where a 10 represents the most energy efficient
		homes. The scale is adjusted for the local climate and utilities, so the
		score number really represents how your home compares to the least and most
		efficient homes in your city.
	</p>

	<Image
		src={homeEnergyScore}
		alt="Graph of Home Energy Score from 1 to 10 10 is best by US Energy Department Indow is 6 with $562 of estimated annual savings"
	/>

	<p>
		Anyone can benefit from a home energy score. But, what’s driving a lot of
		attention here in Portland, Oregon right now is that the score is required
		for homes listed for sale in starting January 1, 2018. The score will allow
		home buyers to compare similar houses based on long-term operating costs,
		which have not been easy for buyers to see. And there’s evidence buyers are
		interested. A recent National Association of Realtors Sustainability Report
		shows that 71 percent of realtors said information about energy efficiency
		is valuable to clients looking for a new home.
	</p>
	<h2>What is assessed to create the home energy score?</h2>
	<p>
		The score is a rating of the home based on its building characteristics —
		the “assets” the home contains. For example, assets such as the type, age
		and condition of the heating system, water heater, ducting and air
		conditioning are important. So is the leakiness of the structure, and
		insulation levels in the attic, floors and walls. Window type and panes are
		a factor, because double and triple-pane windows have a positive impact on a
		home’s score. Roofing type and color are also factors.
	</p>
	<p>
		The size of the home is a big influence. Small homes are inherently more
		efficient because there is less space to heat or cool. A large home, even
		one that’s built to be as efficient as possible, will tend to have a lower
		score. It’s like an SUV that uses a lot of fuel. It might be well-built and
		comfortable, but it just can’t operate as efficiently as a compact hybrid.
	</p>
	<p>
		A home’s utilities are factors (whether your home uses gas, electric or
		both, and the source of the power for those utilities). There’s a
		calculation that takes into account cost of energy and carbon use depending
		on the utility’s energy sources.
	</p>

	<p>If the house has any solar panels, those are also considered.</p>
	<p>
		The score does not consider lighting, appliances or the number of people
		living in the home. The scoring software makes estimates on those items
		based on the size and the number of rooms in the home. This allows a more
		“apples to apples” comparison of the building characteristics themselves,
		rather than how the home was used by previous owners.
	</p>
	<h2>What you can do to improve your score.</h2>
	<p>
		Fortunately, there’s no guesswork when it comes to determining what to do to
		improve a score. Included in the score is a report with recommendations to
		improve efficiency and reduce the home’s carbon footprint. Those
		recommendations can include any combination of upgrades tailored to the
		specific home. Some homes may need an extensive home energy retrofit, while
		others may require just one or two changes.
	</p>
	<p>
		The <a href="/resources/faq/interior-storm-window-inserts-glossary#energy"
			>building envelope</a
		> (the walls, ceiling and floor that together enclose the living space) can range
		from well-insulated and tight to hollow and leaky. Recommendations might include
		installing or adding to wall, attic, floor and ceiling insulation, and sealing
		up leaks.
	</p>
	<p>
		Using interior window inserts like Indow inserts, or exterior storm windows,
		can favorably impact the score because they essentially convert single-pane
		wood or metal windows to double-pane. If all the windows are fitted with <a
			href="/">window inserts</a
		> or storms, it can positively influence the score.
	</p>
	<p>
		Mechanical systems like heating systems, air conditioners, duct work and
		water heaters are sometimes targeted for upgrades. New systems are far more
		efficient than older, inefficient models. High-efficiency technologies like
		heat pumps and ductless mini-splits can make a big difference in efficiency
		and comfort.
	</p>
	<h2>Getting started with energy-saving improvements.</h2>
	<p>
		Because most energy efficiency work requires specialized knowledge, you’ll
		most likely want to work with a contractor to find out what it will take to
		implement the recommendations that come with the score’s report.
	</p>
	<p>
		Look for contractors with staff certified by the Building Performance
		Institute (BPI) and are Home Performance with ENERGY STAR qualified. These
		contractors are trained in techniques to help you get the most out of energy
		saving upgrades. If your home is located in Oregon, search for contractors
		who are Trade Allies of the Energy Trust. Another option is to contact
		Enhabit for independent non-profit advice and access to certified, quality
		contractors.
	</p>

	<p>
		<a href="/how-to/get-indow-inserts">Get A Free Estimate</a>
	</p>
</Section>
